/**
 * The paging and naming behind a lazily-loaded filter picker.
 *
 * One vocabulary too large to hand the browser is asked for a page at a time:
 * a first page when the picker opens, a fresh first page when the typed text
 * settles, another page when the reader reaches the end of the list. Separately
 * it names whatever is already chosen, which may live on a page this picker
 * will never load — without that name a filter arriving in a shared link reads
 * as a bare id and cannot be undone with any confidence.
 *
 * Both the desktop chip popover and the mobile filter sheet run on this, so the
 * two cannot drift on what they offer or when they ask for it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { appendPage, withSelectedOption } from './asyncSelectOptions';

// Long enough that a typist is not firing a request per keystroke, short enough
// that the list feels like it is tracking the text. Matches ScopeSearchInput.
export const SEARCH_DEBOUNCE_MS = 250;
export const DEFAULT_PAGE_SIZE = 25;

/**
 * @param {object} params
 * @param {boolean} params.active - whether the picker is open; nothing is fetched while false.
 * @param {?string} params.value - the chosen option's value, or null.
 * @param {(query: { search: string, offset: number, limit: number, signal: AbortSignal })
 *   => Promise<{ options: { value: string, label: string }[], total: number }>} params.fetchPage
 * @param {?(value: string, context: { signal: AbortSignal })
 *   => Promise<?{ value: string, label: string }>} [params.resolveValue] - names a
 *   selection no loaded page carries.
 * @param {?string} [params.selectedLabel] - the selection's label when the caller has it.
 * @param {number} [params.pageSize]
 * @returns {{ draft: string, setDraft: (text: string) => void, options: object[],
 *   selected: ?object, loading: boolean, hasMore: boolean, remaining: number,
 *   loadMore: () => void, reset: () => void }}
 */
export default function useAsyncFilterOptions({
  active,
  value,
  fetchPage,
  resolveValue = null,
  selectedLabel = null,
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  const [draft, setDraftText] = useState('');
  const [search, setSearch] = useState('');
  const [loaded, setLoaded] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const debounceRef = useRef(null);
  // Bumped per request so a slow page cannot overwrite a newer one.
  const requestIdRef = useRef(0);

  const setDraft = useCallback(text => {
    setDraftText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(text.trim()), SEARCH_DEBOUNCE_MS);
  }, []);

  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setDraftText('');
    setSearch('');
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Name the current selection. A caller-supplied label wins; only a value
  // nothing can name costs a request.
  useEffect(() => {
    if (value == null) {
      setSelected(null);
      return undefined;
    }
    if (selectedLabel) {
      setSelected({ value, label: selectedLabel });
      return undefined;
    }
    if (!resolveValue) return undefined;
    const controller = new AbortController();
    resolveValue(value, { signal: controller.signal })
      .then(option => {
        if (!controller.signal.aborted && option) setSelected(option);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [value, selectedLabel, resolveValue]);

  // The first page for the current text.
  useEffect(() => {
    if (!active) return undefined;
    const myId = ++requestIdRef.current;
    const controller = new AbortController();
    setLoading(true);
    fetchPage({ search, offset: 0, limit: pageSize, signal: controller.signal })
      .then(page => {
        if (requestIdRef.current !== myId) return;
        setLoaded(page?.options ?? []);
        setTotal(page?.total ?? 0);
        setLoading(false);
      })
      .catch(() => {
        if (requestIdRef.current !== myId) return;
        setLoaded([]);
        setTotal(0);
        setLoading(false);
      });
    return () => controller.abort();
  }, [active, search, pageSize, fetchPage]);

  const loadMore = useCallback(() => {
    const myId = ++requestIdRef.current;
    const controller = new AbortController();
    setLoading(true);
    fetchPage({ search, offset: loaded.length, limit: pageSize, signal: controller.signal })
      .then(page => {
        if (requestIdRef.current !== myId) return;
        setLoaded(held => appendPage(held, page?.options ?? []));
        setTotal(page?.total ?? 0);
        setLoading(false);
      })
      .catch(() => {
        if (requestIdRef.current !== myId) return;
        setLoading(false);
      });
  }, [fetchPage, loaded.length, pageSize, search]);

  // The selection leads the list, so it is always visible and always clearable.
  const options = useMemo(() => withSelectedOption(loaded, selected), [loaded, selected]);

  return {
    draft,
    setDraft,
    options,
    selected,
    loading,
    hasMore: loaded.length < total,
    remaining: Math.max(total - loaded.length, 0),
    loadMore,
    reset,
  };
}
